/**
 * In-app rear camera for PWA. Prefer this over @ionic/pwa-elements which often
 * opens an ultrawide / low-res getUserMedia stream (fisheye + washed colors).
 *
 * Focus / luminosity use MediaStreamTrack constraints when the device exposes
 * them; otherwise luminosity falls back to a CSS filter applied at capture time.
 */

import type { CropPictureHeaderMeta } from '../crop-picture-gallery/crop-picture-header';
import { applyCropPictureHeaderOverlay } from '../crop-picture-gallery/crop-picture-header';
import { saveLocalCropPictureArchive } from '../crop-picture-gallery/local-crop-picture-archives';
import { saveCropPictureWithUserGesture } from '../crop-picture-gallery/save-crop-picture-to-gallery';

const IDEAL_WIDTH = 1920;
const IDEAL_HEIGHT = 1080;

export type WebCameraCaptureOptions = {
  /** When set (HL mode), prompt to save a stamped copy before closing. */
  archiveMeta?: CropPictureHeaderMeta;
};

type CapabilityRange = {
  min?: number;
  max?: number;
  step?: number;
};

type TrackCapabilities = {
  focusMode?: string[];
  focusDistance?: CapabilityRange;
  pointsOfInterest?: boolean;
  exposureMode?: string[];
  exposureCompensation?: CapabilityRange;
  brightness?: CapabilityRange;
  zoom?: CapabilityRange;
};

type ImageCaptureCtor = new (track: MediaStreamTrack) => {
  takePhoto: (settings?: { fillLightMode?: string }) => Promise<Blob>;
};

function getImageCapture(track: MediaStreamTrack) {
  const Ctor = (window as Window & { ImageCapture?: ImageCaptureCtor })
    .ImageCapture;
  return Ctor ? new Ctor(track) : null;
}

function getTrackCapabilities(track: MediaStreamTrack): TrackCapabilities {
  const caps = (
    track as MediaStreamTrack & {
      getCapabilities?: () => TrackCapabilities;
    }
  ).getCapabilities?.();
  return caps ?? {};
}

async function applyAdvancedConstraints(
  track: MediaStreamTrack,
  advanced: Record<string, unknown>
): Promise<void> {
  await track.applyConstraints({
    // Chromium expects Image Capture constraints in `advanced`.
    advanced: [advanced],
  } as MediaTrackConstraints);
}

/**
 * Map a tap on the video element to normalized [0,1] coords inside the actual
 * video frame (object-fit: contain letterboxes must be excluded).
 */
function mapTapToVideoNorm(
  video: HTMLVideoElement,
  clientX: number,
  clientY: number
): { x: number; y: number } | null {
  const rect = video.getBoundingClientRect();
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh || rect.width <= 0 || rect.height <= 0) {
    return null;
  }

  const videoRatio = vw / vh;
  const elemRatio = rect.width / rect.height;
  let contentW: number;
  let contentH: number;
  let offsetX: number;
  let offsetY: number;

  if (elemRatio > videoRatio) {
    contentH = rect.height;
    contentW = contentH * videoRatio;
    offsetX = (rect.width - contentW) / 2;
    offsetY = 0;
  } else {
    contentW = rect.width;
    contentH = contentW / videoRatio;
    offsetX = 0;
    offsetY = (rect.height - contentH) / 2;
  }

  const x = (clientX - rect.left - offsetX) / contentW;
  const y = (clientY - rect.top - offsetY) / contentH;
  if (x < 0 || x > 1 || y < 0 || y > 1) {
    return null;
  }

  return { x, y };
}

/** Best-effort tap-to-focus / tap-to-expose for Chrome Android. */
async function applyTapFocus(
  track: MediaStreamTrack,
  point: { x: number; y: number }
): Promise<boolean> {
  const caps = getTrackCapabilities(track);
  const focusModes = caps.focusMode ?? [];
  const exposureModes = caps.exposureMode ?? [];

  const focusMode = focusModes.includes('single-shot')
    ? 'single-shot'
    : focusModes.includes('continuous')
    ? 'continuous'
    : focusModes.includes('manual')
    ? 'manual'
    : undefined;

  const exposureMode = exposureModes.includes('continuous')
    ? 'continuous'
    : exposureModes.includes('single-shot')
    ? 'single-shot'
    : exposureModes.includes('manual')
    ? 'manual'
    : undefined;

  const attempts: Record<string, unknown>[] = [
    {
      ...(focusMode ? { focusMode } : {}),
      ...(exposureMode ? { exposureMode } : {}),
      pointsOfInterest: [point],
    },
    {
      focusMode: 'single-shot',
      pointsOfInterest: [point],
    },
    {
      focusMode: 'continuous',
      pointsOfInterest: [point],
    },
    { pointsOfInterest: [point] },
  ];

  for (const advanced of attempts) {
    try {
      await applyAdvancedConstraints(track, advanced);
      return true;
    } catch {
      // Try next shape — device support varies a lot on Android WebView/Chrome.
    }
  }

  return false;
}

/** Score camera labels — higher = more likely the main rear lens (not ultrawide). */
function scoreCameraLabel(label: string): number {
  const normalized = label.toLowerCase();

  if (isLikelyFrontCamera(normalized)) {
    return -100;
  }

  let score = 0;

  if (
    /back|rear|arrière|environment|main|standard|1x|caméra arrière|camera 0\b/i.test(
      normalized
    )
  ) {
    score += 12;
  }
  if (/tele|zoom|2x|3x|periscope/i.test(normalized)) {
    score += 4;
  }
  if (isLikelyUltraWide(normalized)) {
    score -= 25;
  }
  if (/macro/i.test(normalized)) {
    score -= 10;
  }
  // Generic "wide" without ultra/super often still means ultrawide on Android OEM labels.
  if (/wide|grand angle|angle large/i.test(normalized) && !/tele/i.test(normalized)) {
    score -= 8;
  }

  return score;
}

function isLikelyUltraWide(label: string): boolean {
  return /ultra|super.?wide|superwide|\buw\b|fisheye|0[,.]5\s*x?|0\.5x|très.?grand|very.?wide/i.test(
    label
  );
}

function isLikelyFrontCamera(label: string): boolean {
  return /front|user|selfie|facetime|caméra avant/i.test(label);
}

type CameraProbe = {
  deviceId: string;
  label: string;
  width: number;
  height: number;
  zoomMin: number;
};

function scoreCameraProbe(probe: CameraProbe): number {
  let score = probe.label ? scoreCameraLabel(probe.label) : 0;

  // Unified multi-cam streams: min zoom < 1 ⇒ ultrawide lens available.
  if (probe.zoomMin < 1) {
    score -= 20;
  } else if (probe.zoomMin >= 1) {
    score += 12;
  }

  const aspect = probe.width / Math.max(probe.height, 1);
  // Main rear sensors are usually closer to 4:3 than ultrawide 16:9 previews.
  if (aspect >= 1.2 && aspect <= 1.45) {
    score += 6;
  }
  if (aspect > 1.75) {
    score -= 5;
  }

  return score;
}

async function probeCameraDevice(deviceId: string): Promise<CameraProbe | null> {
  let stream: MediaStream | undefined;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        deviceId: { exact: deviceId },
        width: { ideal: 1280 },
        aspectRatio: { ideal: 4 / 3 },
      },
    });
    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings() as {
      width?: number;
      height?: number;
    };
    const caps = getTrackCapabilities(track);
    return {
      deviceId,
      label: track.label || '',
      width: settings.width ?? 0,
      height: settings.height ?? 0,
      zoomMin: caps.zoom?.min ?? 1,
    };
  } catch {
    return null;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}

async function ensureCameraPermissionForLabels(): Promise<void> {
  let tempStream: MediaStream | undefined;
  try {
    tempStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } },
      audio: false,
    });
  } catch {
    // Continue — labels may stay empty.
  } finally {
    tempStream?.getTracks().forEach((track) => track.stop());
  }
}

/** Prefer the main rear camera; probe devices when labels are missing or ambiguous. */
async function pickRearCameraDeviceId(): Promise<string | undefined> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return undefined;
  }

  await ensureCameraPermissionForLabels();

  const devices = (await navigator.mediaDevices.enumerateDevices()).filter(
    (device) => device.kind === 'videoinput'
  );

  if (devices.length === 0) {
    return undefined;
  }

  const rankedByLabel = devices
    .map((device) => ({
      deviceId: device.deviceId,
      label: device.label || '',
      score: device.label ? scoreCameraLabel(device.label) : null,
    }))
    .filter((entry) => entry.score === null || entry.score > -50);

  const clearLabelWinner = rankedByLabel
    .filter((entry) => entry.score !== null && entry.score >= 8)
    .sort((a, b) => b.score! - a.score!)[0];

  if (clearLabelWinner?.deviceId) {
    return clearLabelWinner.deviceId;
  }

  const probeCandidates = rankedByLabel.slice(0, 4);
  const probes = (
    await Promise.all(
      probeCandidates.map((entry) => probeCameraDevice(entry.deviceId))
    )
  ).filter((probe): probe is CameraProbe => probe !== null);

  if (probes.length > 0) {
    probes.sort((a, b) => scoreCameraProbe(b) - scoreCameraProbe(a));
    return probes[0].deviceId;
  }

  const fallback = rankedByLabel
    .filter((entry) => entry.score === null || entry.score > 0)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0];

  return fallback?.deviceId ?? devices[0]?.deviceId;
}

function buildVideoConstraints(deviceId?: string): MediaTrackConstraints {
  const base: MediaTrackConstraints = {
    width: { ideal: IDEAL_WIDTH },
    height: { ideal: IDEAL_HEIGHT },
    aspectRatio: { ideal: 4 / 3 },
  };

  if (deviceId) {
    return { ...base, deviceId: { exact: deviceId } };
  }

  return { ...base, facingMode: { ideal: 'environment' } };
}

/** Force 1× when the stream exposes ultrawide (min zoom < 1). */
function getMainLensZoom(track: MediaStreamTrack): number {
  const caps = getTrackCapabilities(track);
  const settings = (track.getSettings?.() as { zoom?: number } | undefined) ?? {};
  const min = caps.zoom?.min ?? 1;
  const current = settings.zoom ?? min;

  if (min < 1) {
    return Math.max(1, current < 1 ? 1 : current);
  }

  return current >= 1 ? current : 1;
}

/** Mild digital crop when hardware zoom is unavailable but preview looks ultrawide. */
function suggestBarrelCropZoom(
  track: MediaStreamTrack,
  video: HTMLVideoElement
): number {
  const caps = getTrackCapabilities(track);
  if (caps.zoom) {
    return 1;
  }

  const aspect = video.videoWidth / Math.max(video.videoHeight, 1);
  if (aspect > 1.65) {
    return 1.12;
  }

  return 1;
}

async function openCameraStream(): Promise<MediaStream> {
  const deviceId = await pickRearCameraDeviceId();

  const attempts: MediaStreamConstraints[] = [
    { audio: false, video: buildVideoConstraints(deviceId) },
    { audio: false, video: buildVideoConstraints() },
    {
      audio: false,
      video: { facingMode: { ideal: 'environment' } },
    },
  ];

  let lastError: unknown;
  for (const constraints of attempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Unable to open camera');
}

function captureFromVideoElement(
  video: HTMLVideoElement,
  options: { cssBrightness?: number; digitalZoom?: number } = {}
): Promise<Blob> {
  const cssBrightness = options.cssBrightness ?? 1;
  const digitalZoom = Math.max(options.digitalZoom ?? 1, 1);
  const width = video.videoWidth || IDEAL_WIDTH;
  const height = video.videoHeight || IDEAL_HEIGHT;
  const cropW = Math.max(1, Math.round(width / digitalZoom));
  const cropH = Math.max(1, Math.round(height / digitalZoom));
  const sx = Math.round((width - cropW) / 2);
  const sy = Math.round((height - cropH) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    return Promise.reject(new Error('Canvas unavailable'));
  }
  if (cssBrightness !== 1) {
    context.filter = `brightness(${cssBrightness})`;
  }
  // Center-crop then scale up to full canvas size (= digital zoom).
  context.drawImage(video, sx, sy, cropW, cropH, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        canvas.width = 0;
        canvas.height = 0;
        if (!blob) {
          reject(new Error('Failed to encode photo'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.92
    );
  });
}

function blobToFile(blob: Blob): File {
  const type = blob.type || 'image/jpeg';
  const extension = type.includes('png') ? 'png' : 'jpg';
  return new File([blob], `crop-picture.${extension}`, {
    type,
    lastModified: Date.now(),
  });
}

function createSliderRow(labelText: string): {
  row: HTMLDivElement;
  input: HTMLInputElement;
  valueLabel: HTMLSpanElement;
} {
  const row = document.createElement('div');
  row.style.cssText =
    'display:flex;align-items:center;gap:10px;color:#fff;font-size:13px';

  const label = document.createElement('span');
  label.textContent = labelText;
  label.style.cssText = 'min-width:72px;opacity:0.9';

  const input = document.createElement('input');
  input.type = 'range';
  input.style.cssText = 'flex:1;accent-color:#97c416';

  const valueLabel = document.createElement('span');
  valueLabel.style.cssText = 'min-width:36px;text-align:right;opacity:0.8';

  row.append(label, input, valueLabel);
  return { row, input, valueLabel };
}

function showFocusReticle(
  host: HTMLElement,
  clientX: number,
  clientY: number
): void {
  const rect = host.getBoundingClientRect();
  const reticle = document.createElement('div');
  reticle.style.cssText = [
    'position:absolute',
    'width:56px',
    'height:56px',
    'margin-left:-28px',
    'margin-top:-28px',
    'border:2px solid #97c416',
    'border-radius:8px',
    'pointer-events:none',
    'box-sizing:border-box',
    `left:${clientX - rect.left}px`,
    `top:${clientY - rect.top}px`,
    'transition:opacity 0.4s ease',
  ].join(';');
  host.appendChild(reticle);
  window.setTimeout(() => {
    reticle.style.opacity = '0';
  }, 500);
  window.setTimeout(() => reticle.remove(), 900);
}

/**
 * Full-screen in-app camera. Resolves with a File, or null if the user cancels.
 */
export function captureCropPictureWithWebCamera(
  options: WebCameraCaptureOptions = {}
): Promise<File | null> {
  if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(new Error('Camera API unavailable'));
  }

  return new Promise(async (resolve, reject) => {
    let stream: MediaStream | undefined;
    let settled = false;
    /** 1 = neutral; used only when hardware brightness is unavailable. */
    let cssBrightness = 1;
    let useHardwareBrightness = false;
    /** Digital zoom factor (1 = no zoom). Hardware zoom preferred when available. */
    let zoomFactor = 1;
    let useHardwareZoom = false;
    let pinchStartDistance = 0;
    let pinchStartZoom = 1;
    let suppressNextTapFocus = false;

    const cleanup = () => {
      stream?.getTracks().forEach((track) => track.stop());
      stream = undefined;
      overlay.remove();
    };

    const finish = (file: File | null) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(file);
    };

    const fail = (error: unknown) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(error instanceof Error ? error : new Error(String(error)));
    };

    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:100000',
      'display:flex',
      'flex-direction:column',
      'background:#000',
      'color:#fff',
      'font-family:system-ui,sans-serif',
    ].join(';');

    const videoWrap = document.createElement('div');
    videoWrap.style.cssText =
      'position:relative;flex:1;display:flex;align-items:center;justify-content:center;overflow:hidden;background:#000';

    const video = document.createElement('video');
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    video.style.cssText =
      'width:100%;height:100%;object-fit:contain;background:#000';

    const tapHint = document.createElement('div');
    tapHint.textContent = 'Touchez pour focus · pincez pour zoomer';
    tapHint.style.cssText = [
      'position:absolute',
      'left:50%',
      'bottom:12px',
      'transform:translateX(-50%)',
      'padding:6px 10px',
      'border-radius:999px',
      'background:rgba(0,0,0,0.45)',
      'font-size:12px',
      'pointer-events:none',
      'opacity:0.85',
      'white-space:nowrap',
    ].join(';');

    videoWrap.append(video, tapHint);

    const controls = document.createElement('div');
    controls.style.cssText =
      'display:flex;flex-direction:column;gap:10px;padding:12px 16px;background:#111';

    const luminosity = createSliderRow('Lumière');
    luminosity.input.min = '0';
    luminosity.input.max = '100';
    luminosity.input.step = '1';
    luminosity.input.value = '50';
    luminosity.valueLabel.textContent = '50';

    const zoom = createSliderRow('Zoom');
    zoom.input.min = '1';
    zoom.input.max = '5';
    zoom.input.step = '0.1';
    zoom.input.value = '1';
    zoom.valueLabel.textContent = '1.0×';

    controls.append(luminosity.row, zoom.row);

    const footer = document.createElement('div');
    footer.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;gap:16px;padding:16px 24px;padding-bottom:max(16px,env(safe-area-inset-bottom));background:#111';

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.textContent = 'Annuler';
    cancelButton.style.cssText =
      'appearance:none;border:0;background:transparent;color:#fff;font-size:16px;padding:12px';

    const shutter = document.createElement('button');
    shutter.type = 'button';
    shutter.setAttribute('aria-label', 'Prendre une photo');
    shutter.style.cssText = [
      'width:72px',
      'height:72px',
      'border-radius:50%',
      'border:4px solid #fff',
      'background:#c6cdd8',
      'padding:6px',
      'box-sizing:border-box',
    ].join(';');

    const shutterInner = document.createElement('span');
    shutterInner.style.cssText =
      'display:block;width:100%;height:100%;border-radius:50%;background:#fff';
    shutter.appendChild(shutterInner);

    const spacer = document.createElement('span');
    spacer.style.width = '72px';

    footer.append(cancelButton, shutter, spacer);
    overlay.append(videoWrap, controls, footer);
    document.body.appendChild(overlay);

    cancelButton.addEventListener('click', () => finish(null));

    const showArchivePrompt = async (original: File) => {
      stream?.getTracks().forEach((track) => track.stop());
      stream = undefined;
      video.srcObject = null;
      controls.style.display = 'none';
      shutter.style.display = 'none';
      cancelButton.style.display = 'none';
      spacer.style.display = 'none';
      tapHint.style.display = 'none';

      const previewUrl = URL.createObjectURL(original);
      const preview = document.createElement('img');
      preview.src = previewUrl;
      preview.alt = 'Photo capturée';
      preview.style.cssText =
        'width:100%;height:100%;object-fit:contain;background:#000';
      videoWrap.replaceChildren(preview);

      const status = document.createElement('div');
      status.textContent = 'Préparation de la copie…';
      status.style.cssText =
        'color:#fff;font-size:14px;opacity:0.9;text-align:center;flex:1';
      footer.replaceChildren(status);

      let stamped: File;
      try {
        stamped = await applyCropPictureHeaderOverlay(
          original,
          options.archiveMeta!
        );
        await saveLocalCropPictureArchive(stamped, options.archiveMeta!);
      } catch {
        URL.revokeObjectURL(previewUrl);
        finish(original);
        return;
      }

      const saveButton = document.createElement('button');
      saveButton.type = 'button';
      saveButton.textContent = 'Enregistrer sur le téléphone';
      saveButton.style.cssText = [
        'appearance:none',
        'border:0',
        'border-radius:10px',
        'background:#97c416',
        'color:#111',
        'font-size:15px',
        'font-weight:700',
        'padding:14px 16px',
        'flex:1',
      ].join(';');

      const continueButton = document.createElement('button');
      continueButton.type = 'button';
      continueButton.textContent = 'Continuer';
      continueButton.style.cssText = [
        'appearance:none',
        'border:1px solid rgba(255,255,255,0.35)',
        'border-radius:10px',
        'background:transparent',
        'color:#fff',
        'font-size:15px',
        'padding:14px 16px',
        'min-width:110px',
      ].join(';');

      footer.replaceChildren(saveButton, continueButton);

      saveButton.addEventListener('click', async () => {
        saveButton.disabled = true;
        continueButton.disabled = true;
        saveButton.textContent = 'Enregistrement…';
        try {
          await saveCropPictureWithUserGesture(stamped);
        } finally {
          URL.revokeObjectURL(previewUrl);
          finish(original);
        }
      });

      continueButton.addEventListener('click', () => {
        URL.revokeObjectURL(previewUrl);
        finish(original);
      });
    };

    const setupControls = (track: MediaStreamTrack) => {
      const caps = getTrackCapabilities(track);

      const applyZoom = (nextZoom: number) => {
        const min = Number(zoom.input.min) || 1;
        const max = Number(zoom.input.max) || 5;
        zoomFactor = Math.min(max, Math.max(min, nextZoom));
        zoom.input.value = String(zoomFactor);
        zoom.valueLabel.textContent = `${zoomFactor.toFixed(1)}×`;

        if (caps.zoom) {
          useHardwareZoom = true;
          void applyAdvancedConstraints(track, { zoom: zoomFactor }).then(
            () => {
              // Hardware zoom is already in the stream → no CSS scale needed.
              video.style.transform = '';
            },
            () => {
              useHardwareZoom = false;
              video.style.transform = `scale(${zoomFactor})`;
            }
          );
        } else {
          useHardwareZoom = false;
          video.style.transform = zoomFactor === 1 ? '' : `scale(${zoomFactor})`;
        }
      };

      // Luminosity: CSS filter always works for preview + capture. Also try
      // hardware exposure when available (applies to ImageCapture stills).
      luminosity.input.min = '50';
      luminosity.input.max = '150';
      luminosity.input.step = '5';
      luminosity.input.value = '100';
      luminosity.valueLabel.textContent = '100%';
      cssBrightness = 1;
      useHardwareBrightness = false;
      video.style.filter = '';
      video.style.transformOrigin = 'center center';

      luminosity.input.oninput = () => {
        const percent = Number(luminosity.input.value);
        cssBrightness = percent / 100;
        luminosity.valueLabel.textContent = `${percent}%`;
        video.style.filter =
          cssBrightness === 1 ? '' : `brightness(${cssBrightness})`;

        if (caps.exposureCompensation) {
          const { min = -2, max = 2 } = caps.exposureCompensation;
          const mid = (min + max) / 2;
          const span = (max - min) / 2;
          // 50% → min, 100% → mid, 150% → max
          const value = mid + ((percent - 100) / 50) * span;
          useHardwareBrightness = true;
          void applyAdvancedConstraints(track, {
            exposureMode: 'manual',
            exposureCompensation: value,
          }).catch(() => {
            useHardwareBrightness = false;
          });
        } else if (caps.brightness) {
          const { min = 0, max = 100 } = caps.brightness;
          const value = min + ((percent - 50) / 100) * (max - min);
          useHardwareBrightness = true;
          void applyAdvancedConstraints(track, {
            brightness: value,
          }).catch(() => {
            useHardwareBrightness = false;
          });
        } else {
          useHardwareBrightness = false;
        }
      };

      // Zoom: hardware when available, otherwise CSS scale + center-crop capture.
      if (caps.zoom) {
        const { min = 1, max = 5, step = 0.1 } = caps.zoom;
        zoom.input.min = String(Math.max(min, 1));
        zoom.input.max = String(max);
        zoom.input.step = String(step || 0.1);
      } else {
        zoom.input.min = '1';
        zoom.input.max = '4';
        zoom.input.step = '0.1';
      }

      const applyInitialZoom = () => {
        const initial = caps.zoom
          ? getMainLensZoom(track)
          : suggestBarrelCropZoom(track, video);
        applyZoom(initial);
      };

      applyInitialZoom();
      video.addEventListener('loadedmetadata', applyInitialZoom, { once: true });
      zoom.input.oninput = () => applyZoom(Number(zoom.input.value));

      // Pinch-to-zoom on the preview.
      videoWrap.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length === 2) {
            suppressNextTapFocus = true;
            const [a, b] = [event.touches[0], event.touches[1]];
            pinchStartDistance = Math.hypot(
              a.clientX - b.clientX,
              a.clientY - b.clientY
            );
            pinchStartZoom = zoomFactor;
          }
        },
        { passive: true }
      );

      videoWrap.addEventListener(
        'touchmove',
        (event) => {
          if (event.touches.length !== 2 || pinchStartDistance <= 0) {
            return;
          }
          event.preventDefault();
          const [a, b] = [event.touches[0], event.touches[1]];
          const distance = Math.hypot(
            a.clientX - b.clientX,
            a.clientY - b.clientY
          );
          const ratio = distance / pinchStartDistance;
          applyZoom(pinchStartZoom * ratio);
        },
        { passive: false }
      );

      videoWrap.addEventListener(
        'touchend',
        () => {
          pinchStartDistance = 0;
        },
        { passive: true }
      );

      // Continuous autofocus by default when available.
      if (caps.focusMode?.includes('continuous')) {
        void applyAdvancedConstraints(track, {
          focusMode: 'continuous',
        }).catch(() => undefined);
      }

      // Tap-to-focus (native-camera style). Always wire the gesture; capability
      // checks are unreliable on Android Chrome / WebView.
      tapHint.style.display = 'block';
      videoWrap.style.cursor = 'crosshair';
      videoWrap.style.touchAction = 'none';

      videoWrap.addEventListener('pointerup', (event) => {
        if (suppressNextTapFocus) {
          suppressNextTapFocus = false;
          return;
        }

        const point = mapTapToVideoNorm(video, event.clientX, event.clientY);
        if (!point) {
          return;
        }

        showFocusReticle(videoWrap, event.clientX, event.clientY);
        void applyTapFocus(track, point).then((ok) => {
          tapHint.textContent = ok
            ? 'Touchez pour focus · pincez pour zoomer'
            : 'Focus auto · pincez pour zoomer';
        });
      });
    };

    shutter.addEventListener('click', async () => {
      shutter.disabled = true;
      try {
        const track = stream?.getVideoTracks()[0];
        let blob: Blob | undefined;

        // Prefer ImageCapture when no CSS digital zoom / brightness is applied.
        const canUseImageCapture =
          !!track &&
          cssBrightness === 1 &&
          (useHardwareZoom || zoomFactor === 1);

        if (canUseImageCapture) {
          const imageCapture = getImageCapture(track);
          if (imageCapture) {
            try {
              blob = await imageCapture.takePhoto();
            } catch {
              // Fall back to a frame from the preview stream.
            }
          }
        }

        if (!blob) {
          blob = await captureFromVideoElement(video, {
            cssBrightness,
            digitalZoom: useHardwareZoom ? 1 : zoomFactor,
          });
        }

        const original = blobToFile(blob);
        if (options.archiveMeta) {
          await showArchivePrompt(original);
        } else {
          finish(original);
        }
      } catch (error) {
        fail(error);
      } finally {
        shutter.disabled = false;
      }
    });

    try {
      stream = await openCameraStream();
      video.srcObject = stream;
      await video.play().catch(() => undefined);
      const track = stream.getVideoTracks()[0];
      if (track) {
        setupControls(track);
      }
    } catch (error) {
      fail(error);
    }
  });
}

export function isWebCameraCaptureAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  );
}
